'use client'

import type { ViemEthersSigner } from '@/lib/wallet-core/ethers'
import type { WalletCoreSolanaSigner } from '@/lib/wallet-core/types'
import type { PolymarketActiveWallet } from '@/utils/polymarket-wallet-address'
import type { Connection } from '@solana/web3.js'
import { createContext, useContext } from 'react'
import type { LocalAccount, PublicClient, WalletClient } from 'viem'

export interface WalletContextType {
	// Polygon (EVM)
	eoaAddress: `0x${string}` | undefined
	evmAccount: LocalAccount | null
	walletClient: WalletClient | null
	publicClient: PublicClient | null
	ethersSigner: ViemEthersSigner | null
	// Base (EVM)
	baseWalletClient: WalletClient | null
	basePublicClient: PublicClient | null
	// Solana
	solanaAddress: string | undefined
	solanaSigner: WalletCoreSolanaSigner | null
	solanaConnection: Connection | null
	// Status
	isReady: boolean
	isSolanaReady: boolean
	isBaseReady: boolean
	authenticated: boolean
	// Chance user (DB id), populated after /api/user/profile bootstrap
	userId: string | null
	// Polymarket wallet kind. Nulled by WalletProvider for users on the
	// LEGACY_BROKEN_SAFES allow-list so every consumer falls through to the
	// deposit-wallet path.
	polymarketSafe: string | null
	// True once /api/user/profile has resolved (success, error, or unauthenticated).
	// Consumers that branch on `polymarketSafe` (Safe vs deposit-wallet path)
	// MUST gate on this — initial render has polymarketSafe=null which is
	// indistinguishable from "user genuinely has no Safe", causing legacy users
	// to be misrouted to deposit-wallet path until refresh.
	isPolymarketSafeResolved: boolean
	// Active Polymarket funder preference for legacy Safe users
	// ('safe' | 'deposit' | null = legacy default → Safe). Only meaningful
	// when polymarketSafe is non-null.
	polymarketActiveWallet: PolymarketActiveWallet | null
	// Local setter used by the wallet switcher so the toggle takes effect
	// without a full profile re-fetch. The server preference is persisted
	// separately via POST /api/user/polymarket-wallet.
	setPolymarketActiveWallet: (value: PolymarketActiveWallet | null) => void
}

export const WalletContext = createContext<WalletContextType>({
	eoaAddress: undefined,
	evmAccount: null,
	walletClient: null,
	publicClient: null,
	ethersSigner: null,
	baseWalletClient: null,
	basePublicClient: null,
	solanaAddress: undefined,
	solanaSigner: null,
	solanaConnection: null,
	isReady: false,
	isSolanaReady: false,
	isBaseReady: false,
	authenticated: false,
	userId: null,
	polymarketSafe: null,
	isPolymarketSafeResolved: false,
	polymarketActiveWallet: null,
	setPolymarketActiveWallet: () => {},
})

export function useWallet() {
	return useContext(WalletContext)
}
